import { TFunction } from 'i18next';
import * as yup from 'yup';
import { parseMoneyInput } from '../../../utilities/helper/money.helper';

/**
 * Pieces every Finance form's schema is made of, so "at most N characters (now M)" reads
 * the same in the settings, a catalogue item and a price list.
 *
 * Messages are built with the caller's `t`, so a schema is made per render language; the
 * bounds are the backend DTO's, so a form never accepts what the server then refuses.
 */

const length = (value: unknown) => (typeof value === 'string' ? value.trim().length : 0);

/** Empty is fine here: an empty optional field is "not set", and emptiness is judged elsewhere. */
export const maxText = (t: TFunction, max: number) =>
  yup
    .string()
    .default('')
    .test('max', function (value) {
      const now = length(value);
      return now <= max || this.createError({ message: t('validation.maxLength', { max, now }) });
    });

export const nameText = (t: TFunction, min: number, max: number, emptyMessage?: string) =>
  maxText(t, max).test('min', function (value) {
    const now = length(value);
    if (now === 0) return emptyMessage ? this.createError({ message: emptyMessage }) : true;
    return now >= min || this.createError({ message: t('validation.minLength', { min }) });
  });

/** Typed as text, so "1,5" and "" can be told apart from a number before anything is parsed. */
export const decimalText = (t: TFunction, options: { decimals: number; required?: string; min?: number; max?: number }) =>
  yup
    .string()
    .default('')
    .test('number', function (raw) {
      const value = (raw ?? '').trim().replace(',', '.');
      if (!value) return options.required ? this.createError({ message: options.required }) : true;
      if (!/^\d+(\.\d+)?$/.test(value)) return this.createError({ message: t('validation.number') });
      const decimals = value.split('.')[1]?.length ?? 0;
      if (decimals > options.decimals) return this.createError({ message: t('validation.decimals', { count: options.decimals }) });
      const number = Number(value);
      if (options.min !== undefined && number < options.min) return this.createError({ message: t('validation.min', { min: options.min }) });
      if (options.max !== undefined && number > options.max) return this.createError({ message: t('validation.max', { max: options.max }) });
      return true;
    });

/** An amount as the admin types it - 1.200,50 or 1200.5 - read the way MoneyField reads it. */
export const moneyText = (t: TFunction, options: { required?: string } = {}) =>
  yup
    .string()
    .default('')
    .test('money', function (raw) {
      const value = (raw ?? '').trim();
      if (!value) return options.required ? this.createError({ message: options.required }) : true;
      if (!/^[\d.,\s]+$/.test(value)) return this.createError({ message: t('validation.number') });
      // what is left unparsed by now is a third decimal, or a stray separator
      return parseMoneyInput(value) !== undefined || this.createError({ message: t('validation.decimals', { count: 2 }) });
    });

export const parseDecimal = (raw: string): number | null => {
  const value = (raw ?? '').trim().replace(',', '.');
  return value === '' ? null : Number(value);
};

/** Synchronous validation that answers with every message, keyed by field path. */
export const collectErrors = (schema: yup.AnyObjectSchema, values: unknown): Record<string, string> => {
  try {
    schema.validateSync(values, { abortEarly: false });
    return {};
  } catch (error) {
    if (!(error instanceof yup.ValidationError)) throw error;
    const errors: Record<string, string> = {};
    error.inner.forEach(inner => {
      if (inner.path && !errors[inner.path]) errors[inner.path] = inner.message;
    });
    return errors;
  }
};
